import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Col, FormLabel, Row } from "react-bootstrap";
import moment from "moment";
import "./AppointmentBlockList.css";
import Api from "../../../constants/Api";
import Common from "../../../constants/Common";
import axiosInstance from "../../../services/AxiosInstance";
import { useBranch } from "../../../context/BranchContext";
import { errorObjectAlert, successAlert, warnAlert, warnAlertYesOrNo } from "../../components/alert/defaultAlert";
import CustomSelect from "../../components/Forms/Element/CustomSelect";
import InputDate from "../../components/Forms/Element/InputDate";
import FilterAccordion from "../../components/Common/Filter/FilterAccordion";
import GridCustomBase from "../../components/GridCustom/GridCustomBase";
import PageTitle from "../../layouts/PageTitle";

/**
 * Bloqueios de agenda de toda a rede.
 *
 * A agenda só mostra o bloqueio da filial aberta na tela, o que esconde o registro de quem
 * mudou de unidade: o bloqueio fica preso na filial antiga, onde o colaborador já não aparece
 * na lista, e some da filial nova. Esta tela é o caminho para achar e reabrir esses casos,
 * então ela nasce sem filtro de filial e com o colaborador escolhido entre todos, não só
 * entre os da unidade selecionada no topo do sistema.
 */

/** Uma linha de bloqueio por hora do dia é folga suficiente para um período de um mês. */
const DEFAULT_PAGE_SIZE = Common.DEFAULT_ROWS_PER_PAGE;

const AppointmentBlockList = () => {
    const { t } = useTranslation();
    const { selectedBranch } = useBranch();

    const today = new Date();
    // Abre no mês corrente: bloqueio é assunto do presente, e varrer o histórico inteiro
    // por padrão só deixaria a primeira carga lenta.
    const [startDate, setStartDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
    const [endDate, setEndDate] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    const [branchFilter, setBranchFilter] = useState(null);
    const [employeeFilter, setEmployeeFilter] = useState(null);
    const [includeCanceled, setIncludeCanceled] = useState(false);

    const [branchOptions, setBranchOptions] = useState([]);
    const [employeeOptions, setEmployeeOptions] = useState([]);

    const [rows, setRows] = useState([]);
    const [pageable, setPageable] = useState({});
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
    const [showLoading, setShowLoading] = useState(true);

    useEffect(() => {
        axiosInstance.get(`${Api.main.branch}/user`)
            .then((response) => {
                const options = (Array.isArray(response.data) ? response.data : [])
                    .filter((branch) => branch.status === "ACTIVE")
                    .map((branch) => ({ value: branch.id, label: branch.name }));
                setBranchOptions(options);
            })
            .catch(() => setBranchOptions([]));
    }, []);

    // Sem recorte nenhum de propósito: o colaborador que motivou a tela é justamente o que
    // saiu de uma unidade, e filtrar por filial (ou por quem presta serviço, que em cadastro
    // antigo vem nulo) o deixaria fora da lista de quem se procura.
    useEffect(() => {
        axiosInstance.post(`${Api.main.employee}/findByFilters`, { filters: [] })
            .then((response) => {
                const options = (Array.isArray(response.data) ? response.data : [])
                    .map((employee) => ({ value: employee.id, label: employee.fullName }));
                setEmployeeOptions(options);
            })
            .catch(() => setEmployeeOptions([]));
    }, []);

    const executeSearch = useCallback((targetPage, targetSize) => {
        if (!startDate || !endDate) {
            warnAlert(t("period_filter_requires_both_dates"));
            return;
        }

        setShowLoading(true);
        axiosInstance.get(`${Api.appointment.block}/search`, {
            params: {
                startDate: moment(startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                endDate: moment(endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
                includeCanceled,
                page: targetPage,
                size: targetSize,
                ...(branchFilter?.value ? { branchId: branchFilter.value } : {}),
                ...(employeeFilter?.value ? { employeeId: employeeFilter.value } : {})
            }
        })
            .then((response) => {
                setPageable(response.data || {});
                setRows(Array.isArray(response.data?.content) ? response.data.content : []);
            })
            .catch((error) => {
                errorObjectAlert(error);
                setRows([]);
            })
            .finally(() => setShowLoading(false));
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [startDate, endDate, branchFilter, employeeFilter, includeCanceled]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        executeSearch(page, size);
    }, [page, size]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const handleSearch = () => {
        if (page !== 0) {
            setPage(0);
            return;
        }
        executeSearch(0, size);
    };

    const handleClearFilters = () => {
        const now = new Date();
        setStartDate(new Date(now.getFullYear(), now.getMonth(), 1));
        setEndDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
        setBranchFilter(null);
        setEmployeeFilter(null);
        setIncludeCanceled(false);
    };

    /** Duração do bloqueio em minutos, base tanto do rótulo quanto do total de horas. */
    const durationInMinutes = (block) => {
        if (!block?.startDate || !block?.endDate) {
            return 0;
        }
        return Math.max(moment(block.endDate).diff(moment(block.startDate), "minutes"), 0);
    };

    /** "2h30" lê melhor numa coluna estreita do que "150 minutos". */
    const formatDuration = (minutes) => {
        if (!minutes) {
            return "-";
        }
        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        if (!hours) {
            return `${rest}min`;
        }
        return rest ? `${hours}h${String(rest).padStart(2, "0")}` : `${hours}h`;
    };

    const isActive = (block) => !block.cancellationDate;

    const summaryCards = useMemo(() => {
        const activeRows = rows.filter(isActive);
        const blockedMinutes = activeRows.reduce((total, block) => total + durationInMinutes(block), 0);
        // O card que interessa ao caso do colaborador emprestado: bloqueio ativo que não é da
        // filial aberta no topo, ou seja, o que a agenda daqui nunca mostra.
        const otherBranches = selectedBranch?.id
            ? activeRows.filter((block) => String(block.branchId) !== String(selectedBranch.id))
            : [];

        return [
            {
                key: "active",
                className: "block-list__metric-card--active",
                icon: "fa-light fa-lock",
                label: t("active_blocks"),
                value: activeRows.length,
                caption: t("period")
            },
            {
                key: "hours",
                className: "block-list__metric-card--hours",
                icon: "fa-light fa-hourglass-half",
                label: t("blocked_hours"),
                value: formatDuration(blockedMinutes),
                caption: t("active_blocks")
            },
            {
                key: "other",
                className: "block-list__metric-card--other",
                icon: "fa-light fa-shop",
                label: t("blocks_other_branches"),
                value: otherBranches.length,
                caption: selectedBranch?.name || t("all_branches")
            },
            {
                key: "total",
                className: "block-list__metric-card--total",
                icon: "fa-light fa-list",
                label: t("total"),
                value: pageable?.totalElements ?? rows.length,
                caption: includeCanceled ? t("include_canceled") : t("active_blocks")
            }
        ];
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [rows, pageable, selectedBranch, includeCanceled, t]);

    const cancelBlock = (block) => {
        warnAlertYesOrNo(
            `${t("cancel_block")}: ${block.employeeName} - ${block.branchName} - ${moment(block.startDate).format("DD/MM/YYYY HH:mm")}?`,
            () => {
                axiosInstance.patch(`${Api.appointment.block}/${block.id}/cancel`)
                    .then(() => {
                        successAlert(t("block_canceled"));
                        executeSearch(page, size);
                    })
                    .catch((error) => errorObjectAlert(error));
            }
        );
    };

    const includeCanceledOptions = [
        { value: false, label: t("active_blocks") },
        { value: true, label: t("include_canceled") }
    ];

    return (
        <>
            <PageTitle activeMenu={t("agenda_blocks")} motherMenu={t("appointments")} />

            <FilterAccordion onSearch={handleSearch} onClear={handleClearFilters}>
                <Row className="g-3">
                    <Col md={3}>
                        <FormLabel>{t("start")}</FormLabel>
                        <InputDate onChange={(value) => setStartDate(value)} selected={startDate} />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t("end")}</FormLabel>
                        <InputDate onChange={(value) => setEndDate(value)} selected={endDate} />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t("branch")}</FormLabel>
                        <CustomSelect
                            value={branchFilter}
                            options={branchOptions}
                            onChange={(option) => setBranchFilter(option)}
                            placeholder={t("all_branches")}
                            isClearable
                        />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t("employee")}</FormLabel>
                        <CustomSelect
                            value={employeeFilter}
                            options={employeeOptions}
                            onChange={(option) => setEmployeeFilter(option)}
                            placeholder={t("all_employees")}
                            isClearable
                        />
                    </Col>
                    <Col md={3}>
                        <FormLabel>{t("status")}</FormLabel>
                        <CustomSelect
                            value={includeCanceledOptions.find((option) => option.value === includeCanceled)}
                            options={includeCanceledOptions}
                            onChange={(option) => setIncludeCanceled(Boolean(option?.value))}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            {/* Aviso no padrão do sistema: o tema desenha o fundo e a borda do .alert nos dois modos. */}
            <Alert variant="info" className="py-2">
                <i className="fa-solid fa-circle-info me-2"></i>
                {t("agenda_blocks_hint")}
            </Alert>

            <div className="block-list__metrics">
                {summaryCards.map((card) => (
                    <div key={card.key} className={`block-list__metric-card ${card.className}`}>
                        <div className="block-list__metric-head">
                            <span className="block-list__metric-label">{card.label}</span>
                            <span className="block-list__metric-icon">
                                <i className={card.icon}></i>
                            </span>
                        </div>
                        <p className="block-list__metric-value">{showLoading ? "-" : card.value}</p>
                        <p className="block-list__metric-caption">{card.caption}</p>
                    </div>
                ))}
            </div>

            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}
            >
                <table className="table table-list i-table style-1 mb-0 border-0">
                    <thead>
                        <tr>
                            <th>{t("employee")}</th>
                            <th>{t("branch")}</th>
                            <th>{t("start")}</th>
                            <th className="block-list__col--optional">{t("end")}</th>
                            <th className="block-list__col--optional">{t("duration")}</th>
                            <th>{t("reason")}</th>
                            <th className="block-list__col--optional">{t("created_by")}</th>
                            <th>{t("actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && !showLoading && (
                            <tr>
                                <td colSpan={8} className="block-list__empty">{t("no_blocks_found")}</td>
                            </tr>
                        )}
                        {rows.map((block) => {
                            // Bloqueio de outra unidade é o que a agenda daqui não mostra, então
                            // ganha destaque na linha: é o que o operador está procurando.
                            const fromAnotherBranch = selectedBranch?.id
                                && String(block.branchId) !== String(selectedBranch.id);

                            return (
                                <tr key={block.id} className={isActive(block) ? "" : "block-list__row--canceled"}>
                                    <td>
                                        <span className="block-list__cell-main">{block.employeeName || "-"}</span>
                                        {block.notes && (
                                            <span className="block-list__cell-detail">{block.notes}</span>
                                        )}
                                    </td>
                                    <td>
                                        <span className="block-list__cell-main">{block.branchName || "-"}</span>
                                        {fromAnotherBranch && isActive(block) && (
                                            <span className="block-list__tag">{t("blocks_other_branches")}</span>
                                        )}
                                    </td>
                                    <td>
                                        <span className="block-list__cell-main">
                                            {block.startDate ? moment(block.startDate).format("DD/MM/YYYY HH:mm") : "-"}
                                        </span>
                                        {/* No celular as colunas de fim, duração e autoria saem da grade para
                                            caber na tela, então a informação delas vem aqui embaixo. */}
                                        <span className="block-list__cell-detail block-list__cell-detail--mobile">
                                            {block.endDate ? `até ${moment(block.endDate).format("HH:mm")}` : ""}
                                            {` · ${formatDuration(durationInMinutes(block))}`}
                                            {block.createdBy ? ` · ${block.createdBy}` : ""}
                                        </span>
                                    </td>
                                    <td className="block-list__col--optional">
                                        {block.endDate ? moment(block.endDate).format("DD/MM/YYYY HH:mm") : "-"}
                                    </td>
                                    <td className="block-list__col--optional">{formatDuration(durationInMinutes(block))}</td>
                                    <td>{block.reason || "-"}</td>
                                    <td className="block-list__col--optional">
                                        <span className="block-list__cell-main">{block.createdBy || "-"}</span>
                                        {block.createdAt && (
                                            <span className="block-list__cell-detail">
                                                {moment(block.createdAt).format("DD/MM/YYYY HH:mm")}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {isActive(block) && (
                                            <Button
                                                type="button"
                                                variant="light"
                                                size="sm"
                                                title={t("cancel_block")}
                                                onClick={() => cancelBlock(block)}
                                            >
                                                <i className="fa-light fa-lock-open"></i>
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </GridCustomBase>
        </>
    );
};

export default AppointmentBlockList;
